import { underscore } from '@ember/string';
import JSONAPIAdapter from '@ember-data/adapter/json-api';
import AdapterError from '@ember-data/adapter/error';
import { cached } from '@glimmer/tracking';
import { getOwner } from '@ember/application';
import { type Registry as Services, service } from '@ember/service';
import { Snapshot } from '@ember-data/store';
import config from 'uplisting-frontend/config/environment';
import type ModelRegistry from 'ember-data/types/registries/model';
import { pluralize } from 'ember-inflector';
import type IndexRoute from 'uplisting-frontend/pods/index/route';
import { AnalyticsEvents } from 'uplisting-frontend/services/analytics';

interface IHeaders {
  Authorization?: string;
}

export type IAdapterOptions = Record<string, string | string[]>;

const ATTRIBUTE_POINTER = '/data/attributes/';

const AdapterErrorWithErrors = AdapterError as unknown as new (
  errors?: object[],
) => Error;

export default class ApplicationAdapter extends JSONAPIAdapter {
  @service session!: Services['session'];
  @service router!: Services['router'];
  @service analytics!: Services['analytics'];

  host = config.API_HOST;
  namespace = 'v2';

  @cached
  get headers(): IHeaders {
    const token =
      this.session?.data?.authenticated?.idToken ||
      this.session?.data?.authenticated?.access_token;

    const headers = {};

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    return headers;
  }

  pathForType<K extends keyof ModelRegistry>(modelName: K): string {
    return underscore(pluralize(modelName as string));
  }

  public handleResponse(
    status: number,
    headers: object,
    payload: object,
    requestData: object,
  ): object {
    if (status === 401) {
      this.session.handleNotAuthorized();

      return {
        status,
      };
    } else if (status === 423) {
      this.session.handleLogout();

      return {
        status,
      };
    } else if (status === 403) {
      const indexRoute = getOwner(this)?.lookup('route:index') as IndexRoute;

      return indexRoute.redirectAfterLogin();
    }

    if (status === 422 && !this.#hasAttributeErrors(payload)) {
      return new AdapterErrorWithErrors(
        (payload as { errors?: object[] })?.errors,
      );
    }

    // Surface server-side API failures in the analytics friction dashboard.
    // The 401/403/423 cases above are expected auth flows, not app errors.
    if (status >= 500) {
      this.#trackApiError(status, payload);
    }

    return super.handleResponse(status, headers, payload, requestData);
  }

  #hasAttributeErrors(payload: object): boolean {
    const errors = (payload as { errors?: { source?: { pointer?: string } }[] })
      ?.errors;

    return (
      Array.isArray(errors) &&
      errors.some((error) =>
        error?.source?.pointer?.startsWith(ATTRIBUTE_POINTER),
      )
    );
  }

  #trackApiError(status: number, payload: object): void {
    this.analytics.trackEvent(AnalyticsEvents.appError, {
      error_type: 'api',
      message: this.#errorMessageFromPayload(payload) ?? `HTTP ${status}`,
      route: this.router.currentURL,
      severity: 'error',
      status,
    });
  }

  #errorMessageFromPayload(payload: object): string | undefined {
    const errors = (
      payload as { errors?: { detail?: string; title?: string }[] }
    ).errors;

    const firstError = errors?.[0];

    return firstError?.detail ?? firstError?.title;
  }

  urlForFindRecord<K extends keyof ModelRegistry>(
    id: string,
    modelName: K,
    snapshot: Snapshot<K>,
  ): string {
    return this.#withAdapterOptions(
      super.urlForFindRecord(id, modelName, snapshot),
      snapshot,
    );
  }

  urlForDeleteRecord<K extends keyof ModelRegistry>(
    id: string,
    modelName: K,
    snapshot: Snapshot<K>,
  ): string {
    return this.#withAdapterOptions(
      super.urlForDeleteRecord(id, modelName, snapshot),
      snapshot,
    );
  }

  #withAdapterOptions<K extends keyof ModelRegistry>(
    url: string,
    snapshot: Snapshot<K>,
  ): string {
    if (!snapshot.adapterOptions) {
      return url;
    }

    const queryParams = this.formatQueryParams(
      snapshot.adapterOptions as IAdapterOptions,
    );

    return `${url}?${queryParams}`;
  }

  protected formatQueryParams(adapterOptions: IAdapterOptions): string {
    return Object.entries(adapterOptions)
      .map(([key, value]) => {
        if (Array.isArray(value)) {
          return value.map((item) => `${key}[]=${item}`).join('&');
        }

        return `${key}=${value}`;
      })
      .join('&');
  }
}

declare module 'ember-data/types/registries/adapter' {
  interface AdapterRegistry {
    application: ApplicationAdapter;
  }
}
